import * as React from 'react';
import { DataGrid, DataGridCtor } from './DataGrid';
import { DataGridColumn } from './DataGrid.Column';
import { Form } from './Form';
import { Row, Column } from './Grid';
import { Res } from 'core/api';
import { whenEscKey, whenEnterKey, debounce } from './Utils';
import Button from './Button';
import { Dialog, DialogActions, showMessage } from './Dialog';
import { TextBox, TextBoxButton } from './TextBox';
import * as TagUtils from './TagUtils';
import { createTagsFilter } from './createTagsFilter';

interface MultiSelectGridProps extends React.HTMLAttributes<MultiSelectGridProps> {
	getItems: () => any[];
	height?: number;
	itemKey: (item: any) => any;
	columns: DataGridColumn<any>[];
	allowedTags?: string[]
	onCreate?: string | (() => void);
	onReload?: string | (() => void);
}


type MultiSelectGridState = {
	filterText: string,
	tags: string[],
	items: any[]
}

class MultiSelectGrid extends React.Component<MultiSelectGridProps, MultiSelectGridState> {

	private dataGrid = React.createRef<DataGrid<any>>();
	private form = React.createRef<Form<MultiSelectGridState>>();
	private mounted = false;

	constructor(props: MultiSelectGridProps) {
		super(props);
		this.state = {
			filterText: '',
			tags: [],
			items: props.getItems()
		};
	}

	componentDidMount() {
		this.mounted = true;
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	getSelectedItems() {
		return this.dataGrid.current!.getSelectedItems();
	}

	render() {

		const Grid: DataGridCtor<any> = DataGrid;

		const doSearch = debounce(async (selectionMode?: 'ALLOW_DEFAULT_SELECTION' | 'DISABLE_DEFAULT_SELECTION') => {
			// Por el debounce podría dispararse esto cuando ya no está montado el componente
			if (!this.mounted)
				return;

			const { filterText, tags } = await this.form.current!.ifValid();

			// Esto es un poco raro porque parte (tags) lo filtro aquí, y parte (text) se filta en el dataGrid,
			// pero el datagrid sabe bien cómo filtrar por text en base a los formats y demás

			this.setState({ items: TagUtils.filterByTags(this.props.getItems(), tags) }, () => {
				this.dataGrid.current!.filterBy(filterText);
				const selected = this.dataGrid.current!.getSelectedItems();
				// Aseguro que siempre hay algo seleccionado si al buscar se ha perdido la selección anterior
				if (!selected.length && selectionMode !== 'DISABLE_DEFAULT_SELECTION')
					this.dataGrid.current!.selectFirstItem();

			});
		});

		const onInputKeyDown = (e: React.KeyboardEvent<unknown>) => {
			this.dataGrid.current?.handleExternalKeyDown(e) || doSearch();
		};

		const openLink = (action: string) => window.open(action);

		const createButton = (action: string | (() => void), iconClassName: string) => {
			const onClick = async (_event: React.MouseEvent<HTMLButtonElement>) => {

				if (typeof action === 'string') {
					openLink(action);
					return;
				}

				action();

				// HACK: No nos gusta nada pero no se nos ocurre otra forma de hacerlo según está montado el MultiSelectList ya que al tener el dialogo
				// de las opciones ya abierto cuando hacen un reload no hay otra forma de repintarlo que no sea recargando los datos a mano, como el dialogo es un
				// componente separado y que no comparte estado con el padre (MultiSelectList) no se repinta automáticamente en el reload.
				// Para ello recargamos los items que invoca de nuevo la funcion this.props.getItems() que carga los nuevos Items
				// inyectados en el modelo desde el servidor del MultiSelectList.
				doSearch('DISABLE_DEFAULT_SELECTION');

			};

			return <TextBoxButton iconClassName={iconClassName} onClick={onClick} />;
		};


		const create = this.props.onCreate ? createButton(this.props.onCreate, 'text-info fa fa-plus') : null;
		const reload = this.props.onReload ? createButton(this.props.onReload, 'text-info fa fa-refresh') : null;

		return <div>
			<Form<MultiSelectGridState> model={this.state} ref={this.form}>
				<TextBox<MultiSelectGridState>
					autoFocus
					autoComplete='off'
					linkTo={x => x.filterText}
					onKeyDown={onInputKeyDown}
					placeholder={Res.WA.WA_SEARCH_PLACE_HOLDER()}>
					{create}
					{reload}
				</TextBox>
				{
					createTagsFilter<MultiSelectGridState>({
						linkTo: x => x.tags,
						maybeTaggableItems: this.props.getItems(),
						onValueChanged: doSearch,
						allowedTags: this.props.allowedTags
					})
				}
			</Form>

			<Row className='m-t' >
				<Column width='full'>
					<Grid
						height={this.props.height || 300}
						itemKey={this.props.itemKey}
						ref={this.dataGrid}
						id={this.props.id || 'datagrid_items'}
						columns={this.props.columns}
						initialData={this.state.items}
						shouldPersistState={false}
						multiSelect={true}
						hideExportButton={true}
						maxHeight={400} />
				</Column>
			</Row>
		</div >;
	}
}

interface MultiSelectDialogProps extends React.HTMLAttributes<MultiSelectDialog> {
	grid: MultiSelectGridProps;
	title: string;
	actions?: DialogActions<any[]>;
}

export class MultiSelectDialog extends React.Component<MultiSelectDialogProps, EmptyState> {

	private itemGrid = React.createRef<MultiSelectGrid>();

	constructor(props: MultiSelectDialogProps) {
		super(props);
		this.onOK = this.onOK.bind(this);
		this.onCancel = this.onCancel.bind(this);
	}

	private async onOK() {
		if (!this.props.actions)
			return;

		const selectedItems = this.itemGrid.current!.getSelectedItems();

		if (selectedItems.length === 0) {
			await showMessage(Res.Msg.MSG_MUST_SELECT_A_VALUE());
			return;
		}

		this.props.actions.closeWith(selectedItems);
	}

	private onCancel() {
		this.props.actions?.cancel();
	}

	render() {

		const onDefaultKeyDown = (e: React.KeyboardEvent<any>) => {
			whenEnterKey(this.onOK)(e);
			whenEscKey(this.onCancel)(e);
		};

		return <Dialog onKeyDown={onDefaultKeyDown}>
			<div className='modal-content'>
				<div className='modal-header'>
					<h4 className='modal-title'>{this.props.title}</h4>
				</div>
				<div className='modal-body'>
					<MultiSelectGrid ref={this.itemGrid} {... this.props.grid} />
				</div>
				<div className='modal-footer'>
					<Button buttonStyle='info' onClick={this.onOK}>{Res.Common.OK_BTN()}</Button>
					<Button onClick={this.onCancel}>{Res.Common.CANCEL_BTN()}</Button>
				</div>
			</div>
		</Dialog>;
	}
}
